import type { OpaqueVideoData } from '@lumen5/luminary';

import { ApiService, type Blueprint } from '../../client';
import type { EnhancedToolResult, ToolDefinition, ToolExecutionContext } from '../types';
import { generateVideoDescriptionFromVideoData } from '../video-state';
import { NO_VIDEO_STATE_MESSAGE } from '../video-state/constants';

import { buildReferenceStateGetter } from './reference-video-transcript';
import { Tool } from './Tool';

import { getOrFetchBlueprint } from 'js/src/data/blueprints/blueprints-operations';
import { isApiError } from 'js/src/types';

interface FetchReferenceVideoArgs {
    video_id?: number;
    blueprint_id?: number;
    template_id?: string;
}

const REFERENCE_VIDEO_HEADER =
    '## Reference Video State\n' +
    '_This is the reference video — the video to imitate. It is NOT the video you are editing._\n\n';

function formatBlueprintSettings(blueprint: Blueprint): string {
    const lines: string[] = [
        `- Default template: ${blueprint.default_template_id}`,
        `- Default aspect ratio: ${blueprint.default_aspect_ratio}`,
        `- Narrative thread: ${blueprint.narrative_thread}`,
        `- Target duration: ${blueprint.default_target_duration_seconds} seconds`,
    ];
    if (blueprint.ai_voice) {
        lines.push(`- AI voice: ${blueprint.ai_voice}`);
    }
    if (blueprint.description) {
        lines.push(`- Description: ${blueprint.description}`);
    }
    if (blueprint.supported_templates.length > 0) {
        lines.push(
            `- Templates with reference examples: ${blueprint.supported_templates.join(', ')}`,
        );
    }
    return lines.join('\n');
}

/**
 * Fetches a reference video — either a blueprint's example video or any video
 * the user can access — and returns its full annotation, so the agent can
 * build a new video that matches the reference's script style and visual
 * choices. The blueprint path additionally returns the blueprint's settings
 * and custom instructions, and works for public blueprints whose example
 * videos belong to other workspaces (a direct video fetch would be denied).
 */
export class FetchReferenceVideoTool extends Tool<FetchReferenceVideoArgs> {
    public static readonly toolName = 'fetch_reference_video';

    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: FetchReferenceVideoTool.toolName,
                description:
                    "Fetch a reference video's complete scene-by-scene annotation (script text, scene designs, media, durations) to imitate its style. Pass blueprint_id when the user selected a blueprint (also returns the blueprint's settings and custom instructions); pass video_id to reference one of the user's own videos directly. Call this BEFORE building a video that should match an existing video's style.",
                parameters: {
                    type: 'object',
                    properties: {
                        video_id: {
                            type: 'number',
                            description:
                                'ID of the video to use as the reference. Use when the user points at a specific video rather than a blueprint. Do not pass together with blueprint_id.',
                        },
                        blueprint_id: {
                            type: 'number',
                            description:
                                'The blueprint whose reference video to fetch. When the user has selected a blueprint, this is provided in their preferences. Do not pass together with video_id.',
                        },
                        template_id: {
                            type: 'string',
                            description:
                                "Only valid with blueprint_id: fetch the blueprint's reference example for this template. Only pass it when you already know which template the new video will use AND the blueprint has an example for it; otherwise omit it to get the blueprint's default example.",
                        },
                    },
                    required: [],
                },
            },
        };
    }

    public override getToolDisplayName(): string {
        return 'Studying reference video';
    }

    public get statusIcon(): string {
        return 'video_library';
    }

    public async execute(
        args: FetchReferenceVideoArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const { video_id: videoId, blueprint_id: blueprintId } = args;

        if (videoId && blueprintId) {
            return {
                result: 'Error: Pass either video_id or blueprint_id, not both.',
                isSuccess: false,
            };
        }
        if (videoId) {
            return this.fetchByVideoId(videoId);
        }
        if (blueprintId) {
            return this.fetchByBlueprintId(blueprintId, args.template_id);
        }
        return {
            result: 'Error: Either video_id or blueprint_id is required.',
            isSuccess: false,
        };
    }

    /**
     * Serializes the reference video into its annotation, or null when the
     * video has no scenes (the serializer's "no video yet" message describes
     * the agent's own video, which is exactly what a reference must not be
     * confused with).
     */
    private async describeReference(
        videoData: OpaqueVideoData,
        transcripts?: Record<string, Record<string, unknown>>,
    ): Promise<string | null> {
        // Overlay the reference video's transcript editors onto the session state so its narration
        // renders with per-scene coverage and pauses — identical to get_video_state. Returns null
        // (→ undefined) when there are no transcripts, leaving a structure-only description.
        const getState = buildReferenceStateGetter(this.getState(), transcripts) ?? undefined;
        const annotation = await generateVideoDescriptionFromVideoData(videoData, {
            header: REFERENCE_VIDEO_HEADER,
            getState,
        });
        return annotation === NO_VIDEO_STATE_MESSAGE ? null : annotation;
    }

    private async fetchByVideoId(videoId: number): Promise<EnhancedToolResult> {
        const videoResponse = await ApiService.videoRetrieve({
            path: { video_id: String(videoId) },
        });
        const video = videoResponse.data;

        const referenceAnnotation = await this.describeReference(
            video.video_data as OpaqueVideoData,
            video.transcripts,
        );
        if (referenceAnnotation === null) {
            return {
                result: `Error: Reference video "${video.name}" (id ${videoId}) has no scenes to imitate. Ask the user for a different reference video, or build without one.`,
                isSuccess: false,
            };
        }

        return {
            result: `Success. Fetched reference video "${video.name}" (id ${videoId}).

${referenceAnnotation}`,
            isSuccess: true,
        };
    }

    private async fetchByBlueprintId(
        blueprintId: number,
        templateId?: string,
    ): Promise<EnhancedToolResult> {
        const blueprint = await this.dispatch(getOrFetchBlueprint(blueprintId));
        if (!blueprint) {
            return {
                result: `Error: Blueprint ${blueprintId} was not found or is not accessible.`,
                isSuccess: false,
            };
        }

        let videoDataJson: string;
        try {
            const videoDataResponse = await ApiService.blueprintsTemplateStyleVideoDataRetrieve({
                path: { id: blueprintId },
                query: templateId ? { template: templateId } : undefined,
            });
            videoDataJson = videoDataResponse.data.video_data;
        } catch (error) {
            // Only a 404 means the blueprint genuinely has no example for the
            // requested template; anything else (network, 5xx, auth) must
            // surface as the failure it is so the agent can retry or report.
            if (!isApiError(error) || error.status !== 404) {
                throw error;
            }
            const availableTemplates =
                blueprint.supported_templates.length > 0
                    ? ` Reference examples exist for these templates: ${blueprint.supported_templates.join(', ')}. Retry with one of those template_ids, or omit template_id to use the blueprint's default.`
                    : '';
            return {
                result: `Error: Blueprint ${blueprintId} ("${blueprint.name}") has no reference video${
                    templateId ? ` for template "${templateId}"` : ''
                }.${availableTemplates}`,
                isSuccess: false,
            };
        }

        const referenceAnnotation = await this.describeReference(
            JSON.parse(videoDataJson) as OpaqueVideoData,
        );
        if (referenceAnnotation === null) {
            return {
                result: `Error: The reference video for blueprint "${blueprint.name}" (id ${blueprintId}) has no scenes to imitate. Ask the user for a different reference, or build without one.`,
                isSuccess: false,
            };
        }

        const customInstructionsSection = blueprint.custom_instructions
            ? `\n\n## Custom Instructions (set by the blueprint owner — always follow these)\n${blueprint.custom_instructions}`
            : '';

        return {
            result: `Success. Fetched the reference video for blueprint "${blueprint.name}" (id ${blueprintId}).

## Blueprint Settings
${formatBlueprintSettings(blueprint)}${customInstructionsSection}

${referenceAnnotation}`,
            isSuccess: true,
        };
    }
}
